/**
 * MarketplaceSettings — instructor-facing page to configure their marketplace profile
 * and view incoming leads.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Check, ToggleLeft, ToggleRight, Mail, Phone, MapPin,
  Inbox, ExternalLink, Save, Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import SpecialtyPicker from "../components/instructor/SpecialtyPicker";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MarketplaceSettings() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [leads, setLeads] = useState({ leads: [], unread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [p, s, l] = await Promise.all([
        axios.get(`${API}/api/marketplace/my-profile`, { withCredentials: true }),
        axios.get(`${API}/api/marketplace/specialties`),
        axios.get(`${API}/api/marketplace/my-leads`, { withCredentials: true }),
      ]);
      setProfile(p.data);
      setSpecialties(s.data.specialties || []);
      setLeads(l.data);
    } catch {
      toast.error("Could not load profile");
    }
    setLoading(false);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/marketplace/my-profile`,
        patch, { withCredentials: true });
      setProfile(data);
      return data;
    } catch {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const saveAll = async () => {
    await save({
      visible: profile.visible,
      bio: profile.bio,
      photo_url: profile.photo_url,
      specialties: profile.specialties,
      accepting_new_pupils: profile.accepting_new_pupils,
    });
    setSaving(false);
    toast.success("Profile saved");
  };

  const toggleSpecialty = (s) => {
    const current = profile.specialties || [];
    const next = current.includes(s) ? current.filter(x => x !== s) : [...current, s];
    setProfile(p => ({ ...p, specialties: next }));
  };

  const markLead = async (leadId, status) => {
    try {
      await axios.put(`${API}/api/marketplace/my-leads/${leadId}`,
        { status }, { withCredentials: true });
      const { data } = await axios.get(`${API}/api/marketplace/my-leads`, { withCredentials: true });
      setLeads(data);
    } catch {
      toast.error("Update failed");
    }
  };

  if (loading) return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (!profile) return null;

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="mkt-settings-back" onClick={() => nav("/instructor")} className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button>
          <p className="flex-1 text-sm font-extrabold text-slate-900 dark:text-white">Marketplace</p>
          {leads.unread_count > 0 && (
            <span data-testid="leads-unread-badge" className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              {leads.unread_count} new
            </span>
          )}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Eligibility banner */}
        {!profile.eligibility?.eligible && (
          <div data-testid="mkt-eligibility-banner" className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4">
            <p className="text-sm font-bold text-amber-800 dark:text-amber-200 mb-1">
              {profile.eligibility?.reason === "not_subscribed"
                ? "Activate a subscription to list"
                : "Not eligible to list yet"}
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {profile.eligibility?.reason === "not_subscribed"
                ? "Only subscribed ADIs and PDIs appear in the marketplace. Subscribe to unlock your public listing and start receiving leads."
                : "Only verified platform-member instructors can appear in search results."}
            </p>
          </div>
        )}
        {profile.eligibility?.eligible && profile.is_trainee && (
          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-3">
            <p className="text-xs text-indigo-700 dark:text-indigo-300">
              <span className="font-bold">Trainee listing:</span> your profile shows a "Trainee" badge so learners know you're a PDI in training.
            </p>
          </div>
        )}

        {/* Visibility toggle */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-extrabold text-slate-900 dark:text-white">List me on marketplace</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Learners in your area can find and contact you.</p>
            </div>
            <button data-testid="mkt-visible-toggle"
              disabled={!profile.eligibility?.eligible}
              onClick={() => {
                if (!profile.eligibility?.eligible) {
                  toast.error("Activate a subscription to list on the marketplace.");
                  return;
                }
                setProfile(p => ({ ...p, visible: !p.visible }));
                save({ visible: !profile.visible });
              }}
              className="text-blue-600 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed">
              {profile.visible ? <ToggleRight className="w-10 h-10" /> : <ToggleLeft className="w-10 h-10 text-slate-400" />}
            </button>
          </div>
          {profile.visible && (
            <div className="mt-3 flex items-start justify-between gap-3 pt-3 border-t border-slate-100 dark:border-slate-700">
              <div>
                <p className="text-xs font-bold text-slate-700 dark:text-slate-200">Accepting new pupils</p>
                <p className="text-[10px] text-slate-500">Turn off when you're full.</p>
              </div>
              <button data-testid="mkt-accepting-toggle"
                onClick={() => { setProfile(p => ({ ...p, accepting_new_pupils: !p.accepting_new_pupils })); save({ accepting_new_pupils: !profile.accepting_new_pupils }); }}
                className="text-emerald-600 flex-shrink-0">
                {profile.accepting_new_pupils ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 text-slate-400" />}
              </button>
            </div>
          )}
        </div>

        {/* Profile edit */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm space-y-3">
          <h2 className="text-base font-extrabold text-slate-900 dark:text-white">Profile</h2>
          <div>
            <Label className="text-xs">Bio</Label>
            <Textarea data-testid="mkt-bio" rows={4} value={profile.bio || ""}
              onChange={e => setProfile({ ...profile, bio: e.target.value })}
              placeholder="A short intro. Years experience, teaching style, what makes you great." />
          </div>
          <div>
            <Label className="text-xs">Photo URL (optional)</Label>
            <Input data-testid="mkt-photo" value={profile.photo_url || ""}
              onChange={e => setProfile({ ...profile, photo_url: e.target.value })}
              placeholder="https://..." />
          </div>
          <div>
            <SpecialtyPicker
              value={profile.specialties || []}
              onChange={(next) => setProfile(p => ({ ...p, specialties: next }))}
              testidPrefix="mkt-spec"
            />
          </div>
          <Button data-testid="mkt-save" onClick={saveAll} disabled={saving}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save profile</>}
          </Button>
        </div>

        {/* Leads inbox */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Inbox className="w-4 h-4 text-blue-600" />
            <h2 className="text-base font-extrabold text-slate-900 dark:text-white">Leads</h2>
          </div>
          {leads.leads.length === 0 && (
            <div className="text-center py-6">
              <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-500">No leads yet. Make sure your profile is visible.</p>
            </div>
          )}
          <div className="space-y-2">
            {leads.leads.map(l => (
              <div key={l.id} data-testid={`lead-${l.id}`} className="bg-slate-50 dark:bg-slate-900 rounded-xl p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-bold text-sm text-slate-900 dark:text-white truncate">{l.name}</p>
                      {l.status === "new" && <span className="bg-red-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase">New</span>}
                      {l.status === "contacted" && <span className="bg-amber-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase">Contacted</span>}
                      {l.status === "converted" && <span className="bg-emerald-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase">Converted</span>}
                      {l.status === "closed" && <span className="bg-slate-400 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase">Closed</span>}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5 flex-wrap">
                      <a href={`mailto:${l.email}`} className="flex items-center gap-0.5 hover:text-blue-600"><Mail className="w-2.5 h-2.5" />{l.email}</a>
                      {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-0.5 hover:text-blue-600"><Phone className="w-2.5 h-2.5" />{l.phone}</a>}
                      {l.postcode && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{l.postcode}</span>}
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">
                    {new Date(l.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                  </span>
                </div>
                <p className="text-xs text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">{l.message}</p>
                <div className="flex gap-1.5 pt-1">
                  {l.status === "new" && (
                    <button data-testid={`lead-mark-contacted-${l.id}`} onClick={() => markLead(l.id, "contacted")}
                      className="text-[10px] font-bold text-blue-600 hover:underline">Mark contacted</button>
                  )}
                  {l.status !== "converted" && l.status !== "closed" && (
                    <button data-testid={`lead-mark-converted-${l.id}`} onClick={() => markLead(l.id, "converted")}
                      className="text-[10px] font-bold text-emerald-600 hover:underline">Converted to pupil</button>
                  )}
                  {l.status !== "closed" && (
                    <button data-testid={`lead-mark-closed-${l.id}`} onClick={() => markLead(l.id, "closed")}
                      className="text-[10px] font-bold text-slate-500 hover:underline">Close</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview link */}
        {profile.visible && (
          <div className="space-y-2">
            <Button data-testid="mkt-preview" onClick={() => nav(`/marketplace/instructor/${profile.user_id}`)}
              variant="outline" className="w-full h-11 rounded-xl">
              <ExternalLink className="w-4 h-4 mr-2" /> Preview my public listing
            </Button>
            <Button
              data-testid="mkt-copy-share-link"
              onClick={() => {
                const shareUrl = `${window.location.origin}/api/marketplace/og/instructor/${profile.user_id}`;
                navigator.clipboard.writeText(shareUrl);
                toast.success("Share link copied — renders beautifully on WhatsApp, Instagram, FB, Twitter.");
              }}
              variant="outline" className="w-full h-11 rounded-xl">
              <ExternalLink className="w-4 h-4 mr-2" /> Copy social-share link
            </Button>
            <p className="text-[10px] text-slate-500 text-center">
              Use the social-share link when posting on WhatsApp, Instagram, Facebook, LinkedIn, Twitter — it renders a proper preview card with your photo, area, and specialties.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
